import {
  isAllowedStaticHeaderUrl,
  isSameStaticHeaderPackageResource,
  resolveStaticHeaderResourceUrl,
} from "./staticHeader";

const ALLOWED_RESOURCE_PROTOCOLS = new Set([
  "data:",
  "https:",
  "mailto:",
  "tel:",
]);
const BLOCKED_PACKAGE_ELEMENTS =
  "base, embed, iframe, link, meta, object, script, style";
const GOOGLE_FONTS_STYLES_ORIGIN = "https://fonts.googleapis.com";
const MENU_OPEN_CLASS = "is-hamburger-open";
const PACKAGE_URL_ATTRIBUTES = [
  "action",
  "data-src",
  "formaction",
  "href",
  "poster",
  "src",
  "xlink:href",
];
const STATIC_HEADER_MENU_ATTRIBUTE = "data-static-header-menu";
const STATIC_HEADER_MENU_TOGGLE_ATTRIBUTE = "data-static-header-menu-toggle";

type PackageStyleNodes = {
  documentFontStyle?: HTMLLinkElement;
  shadowStyles: Array<HTMLLinkElement | HTMLStyleElement>;
};

const activeMounts = new WeakMap<ShadowRoot, object>();

async function fetchPackageText(
  url: string,
  signal: AbortSignal
): Promise<string> {
  const response = await fetch(url, {
    credentials: "omit",
    mode: "cors",
    redirect: "error",
    signal,
  });

  if (!response.ok) {
    throw new Error(`Static header resource returned ${response.status}.`);
  }

  return response.text();
}

function isBlockedPackageAttribute(name: string): boolean {
  const normalizedName = name.toLowerCase();

  return (
    normalizedName.startsWith("on") ||
    ["nonce", "srcdoc"].includes(normalizedName)
  );
}

function copyAttributes(source: Element, target: Element): void {
  Array.from(source.attributes).forEach(({ name, value }) => {
    if (isBlockedPackageAttribute(name)) {
      return;
    }

    target.setAttribute(name, value);
  });
}

function resolvePackageElementUrls(element: Element, packageUrl: string): void {
  PACKAGE_URL_ATTRIBUTES.forEach((attribute) => {
    const value = element.getAttribute(attribute);
    if (!value || value.startsWith("#")) {
      return;
    }

    const resolvedValue = resolveStaticHeaderResourceUrl(value, packageUrl);
    if (!resolvedValue) {
      element.removeAttribute(attribute);
      return;
    }

    const { protocol } = new URL(resolvedValue);
    if (!ALLOWED_RESOURCE_PROTOCOLS.has(protocol)) {
      element.removeAttribute(attribute);
      return;
    }

    element.setAttribute(attribute, resolvedValue);
  });

  if (element.tagName.toLowerCase() === "a") {
    const target = element.getAttribute("target");
    if (target === "_blank") {
      const rel = new Set((element.getAttribute("rel") || "").split(/\s+/));
      rel.add("noopener");
      rel.add("noreferrer");
      element.setAttribute("rel", Array.from(rel).filter(Boolean).join(" "));
    }
  }
}

function sanitizePackageElement(element: Element, packageUrl: string): void {
  Array.from(
    element.querySelectorAll(BLOCKED_PACKAGE_ELEMENTS)
  ).forEach((blockedElement) => blockedElement.remove());

  [element, ...Array.from(element.querySelectorAll("*"))].forEach(
    (packageElement) => {
      Array.from(packageElement.attributes).forEach(({ name }) => {
        if (isBlockedPackageAttribute(name)) {
          packageElement.removeAttribute(name);
        }
      });
      resolvePackageElementUrls(packageElement, packageUrl);
    }
  );
}

function createPackageDocument(
  parsedDocument: Document,
  packageUrl: string
): HTMLHtmlElement {
  const root = document.createElement("html");
  const head = document.createElement("head");
  const body = document.createElement("body");

  copyAttributes(parsedDocument.documentElement, root);
  copyAttributes(parsedDocument.body, body);

  Array.from(parsedDocument.body.childNodes).forEach((sourceNode) => {
    if (
      sourceNode instanceof Element &&
      sourceNode.matches(BLOCKED_PACKAGE_ELEMENTS)
    ) {
      return;
    }

    const packageNode = document.importNode(sourceNode, true);
    if (packageNode instanceof Element) {
      sanitizePackageElement(packageNode, packageUrl);
    }
    body.appendChild(packageNode);
  });

  root.append(head, body);

  return root;
}

function isDocumentFontStylesheetUrl(stylesheetUrl: string): boolean {
  const url = new URL(stylesheetUrl);

  return (
    url.origin === GOOGLE_FONTS_STYLES_ORIGIN &&
    ["/css", "/css2"].includes(url.pathname)
  );
}

function isAllowedStylesheetUrl(
  stylesheetUrl: string,
  packageUrl: string
): boolean {
  return (
    isSameStaticHeaderPackageResource(stylesheetUrl, packageUrl) ||
    isDocumentFontStylesheetUrl(stylesheetUrl)
  );
}

function createPackageStyleNodes(
  parsedDocument: Document,
  packageUrl: string,
  nonce?: string
): PackageStyleNodes {
  let documentFontStyle: HTMLLinkElement | undefined;
  const shadowStyles: Array<HTMLLinkElement | HTMLStyleElement> = [];

  Array.from(parsedDocument.head.querySelectorAll("link, style"))
    .filter((sourceElement) => {
      if (sourceElement.tagName.toLowerCase() === "style") {
        return true;
      }

      return (sourceElement.getAttribute("rel") || "")
        .toLowerCase()
        .split(/\s+/)
        .includes("stylesheet");
    })
    .forEach((sourceElement) => {
      if (sourceElement.tagName.toLowerCase() === "style") {
        const style = document.createElement("style");
        style.textContent = sourceElement.textContent;
        if (nonce) {
          style.nonce = nonce;
        }
        shadowStyles.push(style);
        return;
      }

      const sourceHref = sourceElement.getAttribute("href");
      const href =
        sourceHref && resolveStaticHeaderResourceUrl(sourceHref, packageUrl);

      if (!href || !isAllowedStylesheetUrl(href, packageUrl)) {
        throw new Error("Static header stylesheet is not allowed.");
      }

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      ["crossorigin", "integrity", "media", "referrerpolicy"].forEach(
        (attribute) => {
          const value = sourceElement.getAttribute(attribute);
          if (value) {
            link.setAttribute(attribute, value);
          }
        }
      );
      if (nonce) {
        link.nonce = nonce;
      }

      if (isDocumentFontStylesheetUrl(href)) {
        if (documentFontStyle) {
          throw new Error("Only one static header font stylesheet is allowed.");
        }
        link.setAttribute("data-d4-static-header-font", "");
        documentFontStyle = link;
        return;
      }

      shadowStyles.push(link);
    });

  return { documentFontStyle, shadowStyles };
}

function waitForStylesheet(
  link: HTMLLinkElement,
  signal: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new Error("Static header loading was aborted."));
      return;
    }

    const cleanup = () => {
      link.removeEventListener("load", handleLoad);
      link.removeEventListener("error", handleError);
      signal.removeEventListener("abort", handleAbort);
    };
    const handleLoad = () => {
      cleanup();
      resolve();
    };
    const handleError = () => {
      cleanup();
      reject(new Error("Static header stylesheet could not be loaded."));
    };
    const handleAbort = () => {
      cleanup();
      reject(new Error("Static header loading was aborted."));
    };

    link.addEventListener("load", handleLoad);
    link.addEventListener("error", handleError);
    signal.addEventListener("abort", handleAbort, { once: true });
  });
}

function loadDocumentFontStyle(
  link: HTMLLinkElement | undefined,
  signal: AbortSignal
): { cleanup: () => void; loading: Promise<void> } {
  if (!link) {
    return { cleanup: () => {}, loading: Promise.resolve() };
  }

  const loading = waitForStylesheet(link, signal);
  document.head.appendChild(link);

  return {
    cleanup: () => link.remove(),
    loading,
  };
}

async function loadPackageStyles(
  shadowRoot: ShadowRoot,
  styleNodes: Array<HTMLLinkElement | HTMLStyleElement>,
  signal: AbortSignal
): Promise<void> {
  const stylesheetPromises = styleNodes
    .filter((node): node is HTMLLinkElement => node.tagName === "LINK")
    .map((link) => waitForStylesheet(link, signal));

  styleNodes.forEach((styleNode) => shadowRoot.appendChild(styleNode));

  await Promise.all(stylesheetPromises);
}

function attachStaticHeaderMenu(packageRoot: HTMLHtmlElement): () => void {
  const menu = packageRoot.querySelector(`[${STATIC_HEADER_MENU_ATTRIBUTE}]`);
  const toggles = Array.from(
    packageRoot.querySelectorAll<HTMLElement>(
      `[${STATIC_HEADER_MENU_TOGGLE_ATTRIBUTE}]`
    )
  );

  if (!menu || toggles.length === 0) {
    return () => {};
  }

  const setOpen = (isOpen: boolean) => {
    packageRoot.classList.toggle(MENU_OPEN_CLASS, isOpen);
    toggles.forEach((toggle) =>
      toggle.setAttribute("aria-expanded", String(isOpen))
    );
  };
  const handleToggle = () => {
    setOpen(!packageRoot.classList.contains(MENU_OPEN_CLASS));
  };

  setOpen(false);
  toggles.forEach((toggle) => toggle.addEventListener("click", handleToggle));

  return () => {
    toggles.forEach((toggle) =>
      toggle.removeEventListener("click", handleToggle)
    );
    setOpen(false);
  };
}

function clearShadowRoot(shadowRoot: ShadowRoot): void {
  shadowRoot.replaceChildren();
}

function assertMountIsActive(
  shadowRoot: ShadowRoot,
  mountToken: object,
  signal: AbortSignal
): void {
  if (signal.aborted || activeMounts.get(shadowRoot) !== mountToken) {
    throw new Error("Static header loading was aborted.");
  }
}

function clearActiveMount(shadowRoot: ShadowRoot, mountToken: object): void {
  if (activeMounts.get(shadowRoot) !== mountToken) {
    return;
  }

  activeMounts.delete(shadowRoot);
  clearShadowRoot(shadowRoot);
}

export async function mountStaticHeaderPackage({
  host,
  nonce,
  signal,
  url,
}: {
  host: HTMLElement;
  nonce?: string;
  signal: AbortSignal;
  url: string;
}): Promise<() => void> {
  if (!isAllowedStaticHeaderUrl(url)) {
    throw new Error("Static header URL is not allowed.");
  }

  const shadowRoot = host.shadowRoot || host.attachShadow({ mode: "open" });
  const mountToken = {};
  clearShadowRoot(shadowRoot);
  activeMounts.set(shadowRoot, mountToken);

  let cleanupFontStyle = () => {};
  let cleanupMenu = () => {};
  try {
    const html = await fetchPackageText(url, signal);
    assertMountIsActive(shadowRoot, mountToken, signal);

    const parsedDocument = new DOMParser().parseFromString(html, "text/html");
    const packageRoot = createPackageDocument(parsedDocument, url);
    const styleNodes = createPackageStyleNodes(parsedDocument, url, nonce);
    const documentFontStyle = loadDocumentFontStyle(
      styleNodes.documentFontStyle,
      signal
    );
    cleanupFontStyle = documentFontStyle.cleanup;

    await Promise.all([
      loadPackageStyles(shadowRoot, styleNodes.shadowStyles, signal),
      documentFontStyle.loading,
    ]);
    assertMountIsActive(shadowRoot, mountToken, signal);

    shadowRoot.appendChild(packageRoot);
    cleanupMenu = attachStaticHeaderMenu(packageRoot);

    let cleaned = false;
    return () => {
      if (cleaned) {
        return;
      }
      cleaned = true;
      cleanupMenu();
      cleanupFontStyle();
      clearActiveMount(shadowRoot, mountToken);
    };
  } catch (error) {
    cleanupMenu();
    cleanupFontStyle();
    clearActiveMount(shadowRoot, mountToken);
    throw error;
  }
}
